import React from 'react';
import { Box, ButtonBase } from '@mui/material';
import { listStatusMeta } from 'views/deal_flow_board/components/StatusPill';
import { CountPlaceholder, TweenedNumber } from './useTweenedNumber';

// iOS-style segmented control for Deal Flow statuses: frosted track, glass thumb tinted with the active status colour.
// mode "tabs" (design B) is a tablist with arrow-key navigation; mode "nav" (design A rail) marks aria-current.

// Motion measured frame by frame from the reference video (30fps): thumb reaches the target at ~150ms,
// overshoots ~7% at ~210ms and settles by ~450ms; width never changes. That is a spring with damping 0.65
// (SwiftUI ~ response 0.31s, dampingFraction 0.65), sampled here as a CSS linear() curve.
const SPRING = 'linear(0, 0.0393, 0.137, 0.2668, 0.409, 0.5489, 0.677, 0.7876, 0.8782, 0.9486, 1, 1.0348, 1.0559, 1.066, 1.0679, 1.0643, 1.057, 1.0479, 1.0382, 1.0288, 1.0203, 1.013, 1.0072, 1.0027, 0.9994, 0.9973, 0.996, 0.9954, 0.9954, 0.9957, 1)';
const SPRING_MS = 450;
// Labels and icons cross-fade in step with the thumb and finish when it first reaches the target (no overshoot).
const FADE = '150ms cubic-bezier(0.2, 0, 0, 1)';

const StatusSegmentedControl = ({ statuses = [], counts = {}, activeId, onSelect, mode = 'nav', ariaLabel = 'Deal statuses' }) => {
    const stripRef = React.useRef(null);
    const trackRef = React.useRef(null);
    const itemRefs = React.useRef({});
    const [thumb, setThumb] = React.useState(null);
    // First placement must not animate in from the left edge.
    const [placed, setPlaced] = React.useState(false);

    const statusKey = statuses.map((s) => `${s.value}:${s.label}`).join('|');
    const countKey = statuses.map((s) => counts?.[s.value] ?? '').join('|');

    // Thumb follows the active segment's box; re-measured when labels/counts change width or the track resizes.
    React.useLayoutEffect(() => {
        const measure = () => {
            const el = itemRefs.current[activeId];
            setThumb((prev) => {
                if (!el) return null;
                const next = { left: el.offsetLeft, width: el.offsetWidth };
                return prev && prev.left === next.left && prev.width === next.width ? prev : next;
            });
        };
        measure();
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        if (observer && trackRef.current) observer.observe(trackRef.current);
        return () => observer?.disconnect();
    }, [activeId, statusKey, countKey]);

    React.useEffect(() => {
        if (thumb && !placed) requestAnimationFrame(() => setPlaced(true));
    }, [thumb, placed]);

    // Only the strip's own horizontal scroll moves, never the page.
    React.useEffect(() => {
        const strip = stripRef.current;
        const el = itemRefs.current[activeId];
        if (!strip || !el) return;
        const left = el.offsetLeft;
        const right = left + el.offsetWidth;
        if (left < strip.scrollLeft) strip.scrollLeft = left;
        else if (right > strip.scrollLeft + strip.clientWidth) strip.scrollLeft = right - strip.clientWidth;
    }, [activeId]);

    // A status added after the first render slides in (scale + fade) instead of popping into the strip.
    const initialIdsRef = React.useRef(null);
    if (initialIdsRef.current === null && statuses.length) initialIdsRef.current = new Set(statuses.map((s) => s.value));

    const isTabs = mode === 'tabs';
    const handleKeyDown = (event) => {
        if (!isTabs || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const index = statuses.findIndex((s) => s.value === activeId);
        const last = statuses.length - 1;
        const nextIndex = event.key === 'Home' ? 0
            : event.key === 'End' ? last
                : event.key === 'ArrowRight' ? (index >= last ? 0 : index + 1)
                    : (index <= 0 ? last : index - 1);
        const next = statuses[nextIndex];
        if (next) {
            onSelect(next.value);
            itemRefs.current[next.value]?.focus();
        }
    };

    const activeStatus = statuses.find((s) => s.value === activeId);
    const activeColor = listStatusMeta(activeStatus).color;
    const thumbMotion = placed
        ? `transform ${SPRING_MS}ms ${SPRING}, width ${SPRING_MS}ms ${SPRING}, background-color 280ms ease, border-color 280ms ease, box-shadow 280ms ease`
        : 'none';

    return (
        <Box ref={stripRef} sx={{ maxWidth: '100%', overflowX: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
            <Box
                ref={trackRef}
                role={isTabs ? 'tablist' : undefined}
                aria-label={ariaLabel}
                onKeyDown={handleKeyDown}
                sx={{
                    position: 'relative', display: 'inline-flex', alignItems: 'center', gap: '2px',
                    p: '3px', borderRadius: '999px',
                    // Frosted track: translucent grey, hairline border, a faint inner shade like the reference.
                    bgcolor: 'rgba(242, 244, 247, 0.72)',
                    backdropFilter: 'blur(12px) saturate(160%)',
                    border: '1px solid rgba(16, 24, 40, 0.07)',
                    boxShadow: 'inset 0 1px 2px rgba(16, 24, 40, 0.06)',
                }}
            >
                {thumb && (
                    <Box
                        aria-hidden
                        sx={{
                            position: 'absolute', top: 3, bottom: 3, left: 0, width: thumb.width,
                            transform: `translateX(${thumb.left}px)`,
                            borderRadius: '999px', overflow: 'hidden',
                            // Glass: milky white over a tint of the status colour, blurred backdrop, bright rim.
                            backgroundColor: `color-mix(in srgb, ${activeColor} 16%, rgba(255, 255, 255, 0.62))`,
                            backdropFilter: 'blur(10px) saturate(180%)',
                            border: '1px solid rgba(255, 255, 255, 0.85)',
                            boxShadow: [
                                `0 0 0 1px color-mix(in srgb, ${activeColor} 28%, transparent)`,
                                `0 3px 10px color-mix(in srgb, ${activeColor} 24%, transparent)`,
                                '0 1px 2px rgba(16, 24, 40, 0.10)',
                                'inset 0 1px 0 rgba(255, 255, 255, 0.95)',
                                `inset 0 -1px 1px color-mix(in srgb, ${activeColor} 18%, transparent)`,
                            ].join(', '),
                            transition: thumbMotion,
                            '@media (prefers-reduced-motion: reduce)': { transition: 'background-color 150ms ease' },
                            // Top gloss band across the upper half, inset from the rim, as in the reference.
                            '&::before': {
                                content: '""', position: 'absolute', top: 2, left: 6, right: 6, height: '46%',
                                borderRadius: '999px',
                                background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.85) 0%, rgba(255, 255, 255, 0.15) 100%)',
                                pointerEvents: 'none',
                            },
                        }}
                    />
                )}
                {statuses.map((status) => {
                    const { color, Icon } = listStatusMeta(status);
                    const active = status.value === activeId;
                    return (
                        <ButtonBase
                            key={status.value}
                            ref={(el) => { itemRefs.current[status.value] = el; }}
                            onClick={() => onSelect(status.value)}
                            role={isTabs ? 'tab' : undefined}
                            aria-selected={isTabs ? active : undefined}
                            aria-current={!isTabs && active ? 'true' : undefined}
                            tabIndex={isTabs && !active ? -1 : 0}
                            disableRipple
                            sx={{
                                position: 'relative', zIndex: 1, flexShrink: 0,
                                height: 32, px: '12px', gap: '6px', borderRadius: '999px',
                                fontSize: '0.8125rem', fontWeight: 600, whiteSpace: 'nowrap',
                                color: active ? '#101828' : '#667085',
                                transition: `color ${FADE}`,
                                '& .seg-icon': { opacity: active ? 1 : 0.7, transition: `opacity ${FADE}` },
                                ...(initialIdsRef.current && !initialIdsRef.current.has(status.value) && {
                                    '@keyframes segIn': { from: { opacity: 0, transform: 'scale(0.85)' }, to: { opacity: 1, transform: 'none' } },
                                    animation: `segIn 280ms cubic-bezier(0.05, 0.7, 0.1, 1) both`,
                                    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                                }),
                                '&:hover': { color: active ? '#101828' : '#344054' },
                                '&.Mui-focusVisible': { outline: '2px solid #3538CD', outlineOffset: '1px' },
                            }}
                        >
                            {Icon
                                ? <Icon className="seg-icon" sx={{ fontSize: 16, color }} />
                                : <Box component="span" className="seg-icon" sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: color }} />}
                            {status.label}
                            <Box component="span" sx={{ color: active ? '#344054' : '#98A2B3', fontWeight: 500, fontVariantNumeric: 'tabular-nums', transition: `color ${FADE}` }}>
                                <TweenedNumber value={counts?.[status.value]} placeholder={<CountPlaceholder />} />
                            </Box>
                        </ButtonBase>
                    );
                })}
            </Box>
        </Box>
    );
};

export default StatusSegmentedControl;
